'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEqual } from 'underscore';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormControlWithErrors from 'DesignEditorsUI/components/customWidgetEditor/sidebar/FormControlWithErrors';
import ToolFormControl from 'DesignComponents/tool/ToolFormControl';
import UISelect from 'UIComponents/input/UISelect';
import UITextInput from 'UIComponents/input/UITextInput';
import UIExpandingTextArea from 'UIComponents/input/UIExpandingTextArea';
import CustomWidgetField from 'DesignData/models/CustomWidgetField';
import HubDbTableField from 'ContentComponents/cmv2/fields/HubDb';
import HubDbRowField from 'ContentComponents/cmv2/fields/HubDbRow';
import * as hubDbSelectors from 'ContentData/selectors/hubDb';
import * as HubDbActions from 'ContentData/actions/HubDb';
import EditorTypes from 'ContentUtils/constants/EditorTypes';
import { HUBDB_ROW, HUBDB_TABLE } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { DISPLAY_COLUMN_EXCLUSIONS } from 'ContentUtils/constants/HubDbColumnTypes';
import { COLUMNS_TO_FETCH, DEFAULT, DISPLAY_COLUMNS, DISPLAY_FORMAT, PLACEHOLDER, TABLE_NAME_OR_ID } from 'DesignData/enums/CustomWidgetFieldAttributes';
import SidebarInput from 'DesignEditorsUI/components/shared/sidebar/SidebarInput';
import withDebouncedInput from 'DesignEditorsUI/components/shared/inputs/withDebouncedInput';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.fieldEdit.contentOptions.${key}`;

const DebouncedUIExpandingTextArea = withDebouncedInput(UIExpandingTextArea);
const DebouncedUITextInput = withDebouncedInput(UITextInput);
const builtInColumnNames = {
  NAME: 'hs_name',
  PATH: 'hs_path'
};

function asMutable(list) {
  if (!list) return [];
  return list.asMutable ? list.asMutable() : list;
}

const mapStateToProps = (state, ownProps) => {
  const table_id = 'id';
  return {
    hubDbTable: hubDbSelectors.getHubDbTable(state, {
      id: ownProps.field[TABLE_NAME_OR_ID]
    }),
    isUnpublished: hubDbSelectors.getIsUnpublished(state, {
      [table_id]: ownProps.field[TABLE_NAME_OR_ID]
    })
  };
};

const mapDispatchToProps = {
  fetchHubDbTable: HubDbActions.fetchHubDbTable
};

class HubDbRowContentOptions extends Component {
  constructor(...args) {
    super(...args);

    this.onTableChange = e => {
      const {
        editField
      } = this.props;
      editField({
        [TABLE_NAME_OR_ID]: e.target.value,
        [DEFAULT]: {
          id: null
        }
      });
    };

    this.onColumnsToFetchChange = e => {
      this.props.editField({
        [COLUMNS_TO_FETCH]: e.target.value
      });
    };

    this.onDisplayColumnsChange = e => {
      this.props.editField({
        [DISPLAY_COLUMNS]: e.target.value
      });
    };

    this.onDisplayFormatChange = e => {
      this.props.editField({
        [DISPLAY_FORMAT]: e.target.value
      });
    };

    this.onPlaceholderChange = e => {
      this.props.editField({
        [PLACEHOLDER]: e.target.value
      });
    };

    this.onDefaultChange = value => {
      this.props.editField({
        [DEFAULT]: value
      });
    };
  }

  componentDidUpdate(prevProps) {
    const {
      editField,
      fetchHubDbTable,
      field,
      hubDbTable
    } = this.props;

    if (prevProps.field[TABLE_NAME_OR_ID] !== field[TABLE_NAME_OR_ID] && !hubDbTable) {
      fetchHubDbTable(field[TABLE_NAME_OR_ID]);
    }

    if (!isEqual(prevProps.hubDbTable, hubDbTable)) {
      const fetchColumnsList = asMutable(field[COLUMNS_TO_FETCH]);
      const displayColumnsList = asMutable(field[DISPLAY_COLUMNS]);
      const updatedColumns = {};
      const updatedFetchColumns = this.updateColumns(fetchColumnsList, hubDbTable);
      const updatedDisplayColumns = this.updateColumns(displayColumnsList, hubDbTable);

      if (fetchColumnsList && updatedFetchColumns && !isEqual(fetchColumnsList, updatedFetchColumns)) {
        updatedColumns[COLUMNS_TO_FETCH] = updatedFetchColumns;
      }

      if (displayColumnsList && updatedDisplayColumns && !isEqual(displayColumnsList, updatedDisplayColumns)) {
        updatedColumns[DISPLAY_COLUMNS] = updatedDisplayColumns;
      }

      editField(Object.assign({}, updatedColumns));
    }
  }

  getColumnOptions(shouldAllowAllColumnTypes = true) {
    const {
      columns,
      useForPages
    } = this.props.hubDbTable || {};
    if (!columns) return null;
    const builtInColumns = useForPages ? [{
      text: unescapedText(i18nKey('hubdbrow.builtInColumns.name')),
      value: builtInColumnNames.NAME,
      help: builtInColumnNames.NAME
    }, {
      text: unescapedText(i18nKey('hubdbrow.builtInColumns.path')),
      value: builtInColumnNames.PATH,
      help: builtInColumnNames.PATH
    }] : [];
    return columns.reduce((acc, column) => {
      if (shouldAllowAllColumnTypes || !DISPLAY_COLUMN_EXCLUSIONS[column.type]) {
        acc.push({
          text: column.label,
          value: column.name,
          help: column.name
        });
      }

      return acc;
    }, builtInColumns);
  }

  updateColumns(prevColumnNames, hubDbTable) {
    if (!prevColumnNames) return [];
    const {
      columns,
      useForPages
    } = hubDbTable || {};
    const builtInColumns = useForPages ? {
      [builtInColumnNames.NAME]: builtInColumnNames.NAME,
      [builtInColumnNames.PATH]: builtInColumnNames.PATH
    } : {};
    const columnNames = columns.reduce((acc, column) => {
      acc[column.name] = column.name;
      return acc;
    }, builtInColumns);
    return prevColumnNames.filter(prevColumnName => {
      return columnNames[prevColumnName];
    });
  }

  renderHubDBTableSelect() {
    const {
      contentTypes,
      fieldErrors,
      hubDbTable,
      readOnly,
      hostTemplateTypes
    } = this.props;
    return /*#__PURE__*/_jsx(FormControlWithErrors, {
      errors: fieldErrors[TABLE_NAME_OR_ID],
      className: "m-top-3",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('hubdbrow.table')
      }),
      children: /*#__PURE__*/_jsx(SidebarInput, {
        contentTypes: contentTypes,
        disabled: readOnly,
        editor: EditorTypes.DESIGN_MANAGER,
        inputComponent: HubDbTableField,
        isInRowField: true,
        hostTemplateTypes: hostTemplateTypes,
        onChange: this.onTableChange,
        required: true,
        type: HUBDB_TABLE,
        value: hubDbTable && hubDbTable.id
      })
    });
  }

  renderColumnsToFetch() {
    const {
      field,
      hubDbTable,
      isUnpublished,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      className: "m-top-3",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('hubdbrow.columnsToFetch.label')
      }),
      tooltip: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('hubdbrow.columnsToFetch.tooltip1')
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('hubdbrow.columnsToFetch.tooltip2')
          })
        })]
      }),
      tooltipProps: {
        headingText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('hubdbrow.columnsToFetch.tooltipTitle')
        }),
        use: 'longform'
      },
      children: /*#__PURE__*/_jsx(SidebarInput, {
        disabled: readOnly || !(hubDbTable && hubDbTable.id) || isUnpublished,
        inputComponent: UISelect,
        multi: true,
        onChange: this.onColumnsToFetchChange,
        options: this.getColumnOptions(),
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('hubdbrow.columnsToFetch.placeholder')
        }),
        value: asMutable(field[COLUMNS_TO_FETCH])
      })
    });
  }

  renderDisplayColumns() {
    const {
      field,
      hubDbTable,
      isUnpublished,
      readOnly
    } = this.props;
    const shouldAllowAllColumnTypes = false;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      className: "m-top-3",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('hubdbrow.displayColumns.label')
      }),
      tooltip: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('hubdbrow.displayColumns.tooltip1')
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('hubdbrow.displayColumns.tooltip2')
          })
        })]
      }),
      tooltipProps: {
        headingText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('hubdbrow.displayColumns.tooltipTitle')
        }),
        use: 'longform'
      },
      children: /*#__PURE__*/_jsx(SidebarInput, {
        disabled: readOnly || !(hubDbTable && hubDbTable.id) || isUnpublished,
        inputComponent: UISelect,
        multi: true,
        onChange: this.onDisplayColumnsChange,
        options: this.getColumnOptions(shouldAllowAllColumnTypes),
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('hubdbrow.displayColumns.placeholder')
        }),
        required: true,
        value: asMutable(field[DISPLAY_COLUMNS])
      })
    });
  }

  renderColumnsDisplayFormat() {
    const {
      field,
      isUnpublished,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      className: "m-top-3",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('hubdbrow.displayFormat.label')
      }),
      tooltip: /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('hubdbrow.displayFormat.tooltip1')
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('hubdbrow.displayFormat.tooltip2')
          })
        })]
      }),
      tooltipProps: {
        headingText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('hubdbrow.displayFormat.tooltipTitle')
        }),
        use: 'longform'
      },
      children: /*#__PURE__*/_jsx(SidebarInput, {
        disabled: readOnly || !field[DISPLAY_COLUMNS] || isUnpublished,
        inputComponent: DebouncedUIExpandingTextArea,
        maxHeight: 300,
        onChange: this.onDisplayFormatChange,
        placeholder: unescapedText(i18nKey('hubdbrow.displayFormat.placeholder')),
        required: true,
        shrink: true,
        value: field[DISPLAY_FORMAT]
      })
    });
  }

  renderPlaceholderInput() {
    const {
      field,
      hubDbTable,
      isUnpublished,
      readOnly
    } = this.props;
    return /*#__PURE__*/_jsx(ToolFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('selectFields.placeholder.label')
      }),
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('selectFields.placeholder.tooltip')
      }),
      children: /*#__PURE__*/_jsx(SidebarInput, {
        disabled: readOnly || !(hubDbTable && hubDbTable.id) || isUnpublished,
        inputComponent: DebouncedUITextInput,
        onChange: this.onPlaceholderChange,
        value: field[PLACEHOLDER]
      })
    });
  }

  render() {
    const {
      contentTypes,
      field,
      isEmail,
      readOnly,
      hostTemplateTypes
    } = this.props;
    if (!field[TABLE_NAME_OR_ID]) return this.renderHubDBTableSelect();
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [this.renderHubDBTableSelect(), this.renderColumnsToFetch(), this.renderDisplayColumns(), this.renderColumnsDisplayFormat(), this.renderPlaceholderInput(), /*#__PURE__*/_jsx(ToolFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`defaultLabels.${HUBDB_ROW}`)
        }),
        children: /*#__PURE__*/_jsx(SidebarInput, {
          contentTypes: contentTypes,
          columns_to_fetch: field[COLUMNS_TO_FETCH],
          display_columns: field[DISPLAY_COLUMNS],
          display_format: field[DISPLAY_FORMAT],
          editor: EditorTypes.DESIGN_MANAGER,
          inputComponent: HubDbRowField,
          hostTemplateTypes: hostTemplateTypes,
          isInEmail: isEmail,
          onFieldChange: this.onDefaultChange,
          placeholder: field[PLACEHOLDER],
          readOnly: readOnly,
          table_name_or_id: field[TABLE_NAME_OR_ID],
          type: field.type,
          value: field[DEFAULT]
        })
      })]
    });
  }

}

HubDbRowContentOptions.propTypes = {
  contentTypes: PropTypes.array,
  editField: PropTypes.func.isRequired,
  field: PropTypes.instanceOf(CustomWidgetField).isRequired,
  fieldErrors: PropTypes.object,
  hostTemplateTypes: PropTypes.array,
  isEmail: PropTypes.bool,
  readOnly: PropTypes.bool,

  /* Connect */
  fetchHubDbTable: PropTypes.func.isRequired,
  hubDbTable: PropTypes.object,
  isUnpublished: PropTypes.bool
};
export default connect(mapStateToProps, mapDispatchToProps)(HubDbRowContentOptions);